import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react';
import {
  useDataProvider,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import {
  getDefaultValuesForForm,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import { useNavigate } from 'react-router-dom';
import Form from '@product-framework/components/AdvancedForms/Form';
import schema from './create.json';
import validationSchema from './validations';

const FORM_ID = 'CreateServiceProviderCompanyForm';

function ServiceProviderCompanyInfoCreate() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const masterData = useMasterData();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, getApiUrl, get } = useDataProvider(provider);
  const { moduleSettings } = useServiceModule();

  const createSP = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers`;

    let address = {};

    if (data?.billingAddress && data?.billingAddressSessionToken) {
      address = {
        address: {
          addressType: 'MAIN',
          placeId: data?.billingAddress?.id,
          placeSessionToken: data?.billingAddressSessionToken,
        },
      };
    }

    const requestData = {
      ...(data.name && { name: data.name }),
      ...(data.accountNumber && { accountNumber: data.accountNumber }),
      ...(data.status && { status: data.status }),
      ...(data.fein && { fein: data.fein }),
      ...(data.websiteUrl && { websiteUrl: data.websiteUrl }),
      ...(data.bmsSystem && { bmsSystem: data.bmsSystem }),
      ...(data.contactFirstName ||
      data.contactLastName ||
      data.contactEmail ||
      data.contactPhoneNumber
        ? {
            contact: {
              ...(data.contactFirstName && {
                firstName: data.contactFirstName,
              }),
              ...(data.contactLastName && { lastName: data.contactLastName }),
              ...(data.contactEmail && { email: data.contactEmail }),
              ...(data.contactPhoneNumber && {
                phoneNumber: data.contactPhoneNumber,
              }),
            },
          }
        : {}),
      ...address,
      ...(data.defaultServiceTravelDistance && {
        defaultServiceTravelDistance: Number(data.defaultServiceTravelDistance),
      }),
    };
    const response = await post(url, requestData);
    return response;
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      const defaultInitialValues = {
        ...getDefaultValuesForForm({
          schema,
        }),
        defaultServiceTravelDistance:
          masterData?.defaultServiceTravelDistance || null,
        status: 'ACTIVE',
      };
      return { data: defaultInitialValues, errors: null };
    } catch (err) {
      const defaultInitialValues = {
        ...getDefaultValuesForForm({
          schema,
        }),
        status: 'ACTIVE',
      };
      return {
        data: defaultInitialValues,
        errors: [{ message: err.message }],
      };
    }
  }, [masterData?.defaultServiceTravelDistance, getDefaultValuesForForm]);

  const fetchSchema = useCallback(async () => {
    try {
      if (moduleSettings?.dispatchPurchased) {
        const dynamicSchema = schema.map(field => {
          if (field.name === 'defaultServiceTravelDistance') {
            return {
              ...field,
              required: true,
            };
          }
          return field;
        });
        return { data: dynamicSchema };
      }
      return { data: schema };
    } catch (err) {
      return { data: schema, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased]);

  const fetchValidationSchema = useCallback(async () => {
    try {
      const validations = validationSchema(
        formatMessage,
        getApiUrl,
        get,
        moduleSettings?.dispatchPurchased,
      );
      return {
        data: validations,
      };
    } catch (err) {
      return { data: null, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased]);

  const onSubmit = async data => {
    const response = await createSP(data);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_CREATE_SUCCESS',
          defaultMessage: 'ServiceProvider was saved successfully',
        }),
      );

      return {
        nextValues: data,
        errors: null,
        callback: () =>
          navigate(`/serviceproviders/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  return (
    <PageSection
      label={formatMessage({
        id: 'SERVICE_PROVIDER_BASIC_INFO_EDIT_HEADING',
        defaultMessage: 'Service Provider Company Information',
      })}>
      <Form
        formId={FORM_ID}
        ref={formRef}
        permittedByDefault
        onSubmit={onSubmit}
        fetchSchema={fetchSchema}
        fetchDefaultValues={fetchDefaultValues}
        fetchValidationSchema={fetchValidationSchema}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            variant="contained"
            permissionName="CAN_UPDATE_SP_BASIC_INFO"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SERVICEPROVIDER_CREATE_SAVE_BUTTON',
              defaultMessage: 'Save Service Provider',
            })}
          </PermissionButton>
          <PermissionButton
            variant="contained"
            permissionName="CAN_UPDATE_SP_BASIC_INFO"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => {
              formRef.current?.reset();
            }}>
            {formatMessage({
              id: 'SERVICEPROVIDER_CREATE_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </PageSection>
  );
}

export default observer(ServiceProviderCompanyInfoCreate);
